import { useState, useEffect } from "react";
import type { OrderHistoryItem } from "@domain/dtos/models/order.models";
import type { MachinePhoto, MachineTariff } from "@domain/dtos/models/machine.models";
import type { ServiceType } from "@domain/enums/machine.enums";
import { getInfoByService } from "@domain/enums/local/getInfoByService";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@boundary/components/tabs";
import { formatRubles } from "@shared/utils/number/formatRubles";
import { CopyableAddress } from "@boundary/components/copyableAddress";
import { observer } from "mobx-react-lite";
import styles from "./orderContent.module.scss";
import { Swiper, SwiperSlide } from "swiper/react";

interface ImageGalleryProps {
  images: string[];
}

export function ImageGallery({ images }: ImageGalleryProps) {
  const [loadState, setLoadState] = useState<(boolean | null)[]>([]);

  useEffect(() => {
    setLoadState(new Array(images.length).fill(null));
  }, [images]);

  const handleImageLoad = (index: number) => {
    setLoadState(prev => {
      const next = [...prev];
      next[index] = true;
      return next;
    });
  };

  const handleImageError = (index: number) => {
    setLoadState(prev => {
      const next = [...prev];
      next[index] = false;
      return next;
    });
  };

  const allAttempted = loadState.length > 0 && loadState.every(v => v !== null);
  const hasAnyLoaded = loadState.some(v => v === true);

  if (images.length === 0 || (allAttempted && !hasAnyLoaded)) {
    return null;
  }
  
  return (
    <Swiper
      className={`${styles.gallery} ${styles.swiper}`}
      slidesPerView="auto"
    >
      {images.map((image, i) => (
        <SwiperSlide className={styles.galleryItem} key={i} style={{ display: loadState[i] === false ? 'none' : undefined }}>
          <img 
            className={styles.galleryItemImg} 
            src={image} 
            alt=""
            onLoad={() => handleImageLoad(i)}
            onError={() => handleImageError(i)}
          />
        </SwiperSlide>
      ))}
    </Swiper>
  );
}

interface MachineStatsProps {
  workingHours: string;
  status: string;
  freeCells: number;
  machineNumber: string;
}

function MachineStats({ workingHours, status, freeCells, machineNumber }: MachineStatsProps) {
  const openStatusText = status === "online" ? "Открыто" : "Закрыто";
  
  return (
    <div className={styles.stats}>
      <div className={styles.statsItem}>
        <span className={styles.statsLabel}>{workingHours}</span>
        <span className={styles.statsValue}>{openStatusText}</span>
      </div>
      <div className={styles.statsItem}>
        <span className={styles.statsLabel}>Свободных ячеек</span>
        <span className={styles.statsValue}>{freeCells}</span>
      </div>
      <div className={styles.statsItem}>
        <span className={styles.statsLabel}>Станция</span>
        <span className={styles.statsValue}>{machineNumber}</span>
      </div>
    </div>
  );
}

interface OrderMachineInfoProps {
  machineInfo: OrderHistoryItem["machine_info"];
}

function OrderMachineInfo({ machineInfo }: OrderMachineInfoProps) {
  const photos = machineInfo.photos.map((photo: MachinePhoto) => photo.url);
  const hasPhotos = photos.length > 0;
  
  return (
    <div className={styles.machineInfo}>
      <div className={styles.machineHeader}>
        <h2 className={styles.machineName}>{machineInfo.title}</h2>
        <CopyableAddress address={machineInfo.address} />
        {machineInfo.description && (
          <p className={styles.machineDescription}>{machineInfo.description}</p>
        )}
      </div>
      
      <MachineStats
        workingHours={machineInfo.working_hours}
        status={machineInfo.status}
        freeCells={machineInfo.free_cells}
        machineNumber={machineInfo.machine_number}
      />
      
      {hasPhotos && <ImageGallery images={photos} />}
    </div>
  );
}

interface OrderTariffsProps {
  tariffs: OrderHistoryItem["machine_info"]["tariffs"];
}

function OrderTariffs({ tariffs }: OrderTariffsProps) {
  if (tariffs.length === 0) return null;
  
  // Сортируем тарифы по ID
  const sortedTariffs = [...tariffs].sort((a, b) => a.id - b.id);
  
  return (
    <div className={styles.tariffs}>
      <h3 className={styles.tariffsTitle}>Тарифы на хранение</h3>
      <ul className={styles.tariffsList}>
        {sortedTariffs.map((tariff: MachineTariff) => (
          <li key={tariff.id} className={styles.tariffsItem}>{tariff.description}</li>
        ))}
      </ul>
    </div>
  );
}

interface OrderServiceItemProps {
  serviceType: ServiceType;
  totalPrice?: number;
}

function OrderServiceItem({ serviceType, totalPrice }: OrderServiceItemProps) {
  return (
    <div className={styles.cartItem}>
      <span className={styles.cartItemName}>
        {getInfoByService(serviceType).title}
      </span>
      <span className={styles.cartItemPrice}>
        {totalPrice !== undefined ? formatRubles(totalPrice) : "—"}
      </span>
    </div>
  );
}

interface OrderServicesListProps {
  services: OrderHistoryItem["cart"]["services"];
}

function OrderServicesList({ services }: OrderServicesListProps) {
  return (
    <div className={styles.cartList}>
      {services.map((service: { service_type: ServiceType; total_price?: number }, i: number) => (
        <OrderServiceItem
          key={i}
          serviceType={service.service_type}
          totalPrice={service.total_price}
        />
      ))}
    </div>
  );
}

interface OrderTotalProps {
  totalPrice: number;
}

function OrderTotal({ totalPrice }: OrderTotalProps) {
  return (
    <div className={styles.cartTotal}>
      <span className={styles.cartTotalLabel}>Сумма</span>
      <span className={styles.cartTotalValue}>{formatRubles(totalPrice)}</span>
    </div>
  );
}

interface OrderInfoTabProps {
  machineInfo: OrderHistoryItem["machine_info"];
}

function OrderInfoTab({ machineInfo }: OrderInfoTabProps) {
  return (
    <TabsContent className={styles.tabContent} value="info">
      <OrderMachineInfo machineInfo={machineInfo} />
      <OrderTariffs tariffs={machineInfo.tariffs} />
    </TabsContent>
  );
}

interface OrderCartTabProps {
  cart: OrderHistoryItem["cart"];
}

function OrderCartTab({ cart }: OrderCartTabProps) {
  return (
    <TabsContent value="cart" className={styles.tabContent}>
      <OrderServicesList services={cart.services} />
      <OrderTotal totalPrice={cart.total_price} />
    </TabsContent>
  );
}

interface OrderContentProps {
  item: OrderHistoryItem;
}

export const OrderContent = observer(function OrderContent({ item }: OrderContentProps) {
  return (
    <Tabs defaultValue="info" className={styles.tabs}>
      <TabsList>
        <TabsTrigger value="info">Информация</TabsTrigger>
        <TabsTrigger value="cart">Корзина</TabsTrigger>
      </TabsList>

      <OrderInfoTab machineInfo={item.machine_info} />
      <OrderCartTab cart={item.cart} />
    </Tabs>
  );
});
